import PersonOutlineIcon from "@mui/icons-material/PersonOutline";
import { Button, CircularProgress, Tooltip } from "@mui/material";
import { linko } from "@shared/linko";
import { Roles } from "@shared/Roles";
import { isAxiosTypedLinkoError } from "@shared/exceptions-message";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { type FC } from "react";
import { remult } from "remult";
import useQuestion from "../../dialog-components/QuestionDialog";
import { useAlert } from "../../contexts/AlertProvider";
import { useUser } from "../../contexts/UserProvider";
import {
  patchLeadHandlingInList,
  refreshLeadHandlingBadges,
} from "../../queries/invalidate-lead-handling";
import {
  HANDLING_COPY,
  handlingActionButtonSx,
} from "../opportunity-handling/handling-ui";
import { OpportunityHandlingStatusPill } from "../opportunity-handling/OpportunityHandlingStatusPill";
import { LEAD_ACTION_TOOLTIPS } from "../../pages/tables/summer-campaign-leads-table/lead-actions-copy";
import { LeadActionTooltip } from "../../pages/tables/summer-campaign-leads-table/LeadActionTooltip";

const LEAD_COPY = {
  claimError: "לא ניתן לסמן את הליד בטיפול",
  releaseError: "לא ניתן לשחרר את הטיפול",
};

interface Props {
  leadId: string;
  handlingManagerId?: string | null;
  handlingManagerName?: string | null;
  handlingSince?: Date | string | null;
  variant?: "default" | "compact";
  onChanged?: () => void;
}

export const LeadHandlingControls: FC<Props> = ({
  leadId,
  handlingManagerId,
  handlingManagerName,
  handlingSince,
  variant = "default",
  onChanged,
}) => {
  const { currentUser } = useUser();
  const { setAlertText, setSeverity } = useAlert();
  const queryClient = useQueryClient();
  const question = useQuestion();

  const isMine =
    !!handlingManagerId && handlingManagerId === currentUser?.id;
  const isHandledByOther =
    !!handlingManagerId && handlingManagerId !== currentUser?.id;
  const canReleaseOthers = remult.isAllowed(Roles.Admins);
  const pillSize = variant === "compact" ? "sm" : "md";

  const afterHandlingChange = () => {
    refreshLeadHandlingBadges(queryClient);
    onChanged?.();
  };

  const claimMutation = useMutation({
    mutationFn: () =>
      linko.summerCampaignLead.mutations.claimLeadHandling({ leadId }),
    onSuccess: (res) => {
      if (!res.data.success) {
        setSeverity("warning");
        setAlertText(res.data.message);
        if (res.data.handlingManagerId) {
          patchLeadHandlingInList(queryClient, {
            leadId,
            organizationId: "",
            handlingManagerId: res.data.handlingManagerId,
            handlingManagerName: res.data.handlingManagerName ?? null,
            handlingSince: res.data.handlingSince ?? null,
          });
          refreshLeadHandlingBadges(queryClient);
        }
        return;
      }
      setSeverity("success");
      setAlertText(res.data.message);
      if (res.data.handlingManagerId) {
        patchLeadHandlingInList(queryClient, {
          leadId,
          organizationId: "",
          handlingManagerId: res.data.handlingManagerId,
          handlingManagerName: res.data.handlingManagerName ?? null,
          handlingSince: res.data.handlingSince ?? null,
        });
      }
      afterHandlingChange();
    },
    onError: () => {
      setSeverity("error");
      setAlertText(LEAD_COPY.claimError);
    },
  });

  const releaseMutation = useMutation({
    mutationFn: () =>
      linko.summerCampaignLead.mutations.releaseLeadHandling({ leadId }),
    onSuccess: (res) => {
      setSeverity("success");
      setAlertText(res.data.message);
      patchLeadHandlingInList(queryClient, {
        leadId,
        organizationId: "",
        handlingManagerId: null,
        handlingManagerName: null,
        handlingSince: null,
      });
      afterHandlingChange();
    },
    onError: (err: unknown) => {
      setSeverity("error");
      const message = isAxiosTypedLinkoError(err)
        ? err.linkoError.message
        : LEAD_COPY.releaseError;
      setAlertText(message);
    },
  });

  const isPending = claimMutation.isPending || releaseMutation.isPending;

  const handleRelease = async () => {
    if (isHandledByOther) {
      const confirmed = await question(
        HANDLING_COPY.releaseOtherConfirm(
          handlingManagerName ?? "מנהל אחר",
        ),
        "",
        { confirmText: "שחרר", cancelText: "ביטול" },
      );
      if (!confirmed) return;
    }
    releaseMutation.mutate();
  };

  if (isHandledByOther) {
    return (
      <Tooltip title={LEAD_ACTION_TOOLTIPS.treatDisabledOther(handlingManagerName ?? "מנהל אחר")} arrow>
        <span style={{ display: "inline-flex" }}>
          <OpportunityHandlingStatusPill
            tone="other"
            managerName={handlingManagerName}
            handlingSince={handlingSince}
            size={pillSize}
            showRelease={canReleaseOthers}
            releasePending={isPending}
            onRelease={() => void handleRelease()}
            releaseTooltip={LEAD_ACTION_TOOLTIPS.releaseHandling}
          />
        </span>
      </Tooltip>
    );
  }

  if (isMine) {
    return (
      <OpportunityHandlingStatusPill
        tone="mine"
        handlingSince={handlingSince}
        size={pillSize}
        showRelease
        releasePending={isPending}
        onRelease={() => void handleRelease()}
        releaseTooltip={LEAD_ACTION_TOOLTIPS.releaseHandling}
      />
    );
  }

  return (
    <LeadActionTooltip title={LEAD_ACTION_TOOLTIPS.claimHandling}>
      <Button
      size="small"
      variant="outlined"
      color="secondary"
      disabled={isPending}
      onClick={(e) => {
        e.stopPropagation();
        claimMutation.mutate();
      }}
      startIcon={
        isPending ? (
          <CircularProgress size={14} color="inherit" />
        ) : (
          <PersonOutlineIcon fontSize="small" />
        )
      }
      sx={handlingActionButtonSx}
    >
      {HANDLING_COPY.claimAction}
    </Button>
    </LeadActionTooltip>
  );
};
